import { initSentry, isSentryInitialized } from './sentry';
import { initWebVitals } from './telemetry';
import type { ObservabilityConfig } from './types';

/**
 * Initialize observability (Sentry + Web Vitals)
 *
 * Call this once at the top of your application, before rendering React.
 *
 * Usage:
 * ```typescript
 * import { initObservability } from '@commander/observability';
 *
 * initObservability({
 *   dsn: import.meta.env.VITE_SENTRY_DSN,
 *   environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
 *   release: import.meta.env.VITE_SENTRY_RELEASE,
 *   tracesSampleRate: 0.1,
 *   enableWebVitals: true,
 * });
 *
 * // Then render your app
 * createRoot(document.getElementById('root')!).render(<App />);
 * ```
 */
export function initObservability(config: ObservabilityConfig): boolean {
  // Initialize Sentry
  const sentryInitialized = initSentry(config);

  // Initialize Web Vitals if Sentry is initialized and enabled
  if (sentryInitialized && config.enableWebVitals !== false) {
    initWebVitals();
  }

  return sentryInitialized;
}

/**
 * Check if observability is initialized
 */
export function isObservabilityInitialized(): boolean {
  return isSentryInitialized();
}
